import React, { useState, useEffect } from 'react';
import { 
  getAdminStats, getAdminRequests, getAdminApplications, getAdminProjects, 
  updateApplicationStatus, updateMentorRequestStatus, updateProjectStatus, 
  getMentorStats, getUserStats 
} from '../services/adminService';
import { useAuth } from '../context/AuthContext';
import { useAlertContext } from '../context/AlertContext';
import { FaUserTie, FaUserGraduate, FaCrown, FaClipboardList, FaCheck, FaTimes, FaSpinner, FaCodeBranch, FaExternalLinkAlt, FaGithub } from 'react-icons/fa';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [applications, setApplications] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { showAlert } = useAlertContext();

  // Active view state
  const [activeView, setActiveView] = useState('dashboard');
  const [mentorStats, setMentorStats] = useState([]);
  const [userStats, setUserStats] = useState([]);

  // Project Modal State
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, requestsRes, applicationsRes] = await Promise.all([
        getAdminStats(),
        getAdminRequests(),
        getAdminApplications()
      ]);

      if (statsRes.success) {
        setStats(statsRes.stats);
      }
      if (requestsRes.success) {
        setRequests(requestsRes.requests);
      }
      if (applicationsRes.success) {
        setApplications(applicationsRes.applications);
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      showAlert({ type: 'error', title: 'Error', message: 'Failed to fetch admin dashboard data' });
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
      try {
        setLoading(true);
        setLoading(true);
        const data = await getAdminProjects();
        if (data.success) {
            setProjects(data.data);
            setActiveView('projects');
        }
      } catch (error) {
          console.error('Error fetching projects:', error);
          showAlert({ type: 'error', message: 'Failed to fetch projects' });
      } finally {
          setLoading(false);
      }
  };

  const handleApplicationStatus = async (appId, newStatus) => {
    try {
      await updateApplicationStatus(appId, newStatus);
      
      showAlert({ type: 'success', title: 'Success', message: `Application ${newStatus}` });
      fetchData(); 
    } catch (error) {
      console.error('Error updating application status:', error);
      showAlert({ type: 'error', title: 'Error', message: 'Failed to update application status' });
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await updateMentorRequestStatus(requestId, newStatus);
      
      showAlert({ type: 'success', title: 'Success', message: `Request marked as ${newStatus}` });
      fetchData(); 
    } catch (error) {
      console.error('Error updating status:', error);
      showAlert({ type: 'error', title: 'Error', message: 'Failed to update request status' });
    }
  };

  const handleProjectStatus = async (projectId, newStatus) => {
      try {
        await updateProjectStatus(projectId, newStatus);
        showAlert({ type: 'success', message: `Project ${newStatus}` });
        // Refresh local list
        setProjects(prev => prev.map(p => p._id === projectId ? { ...p, status: newStatus } : p));
        // Update selected project if open
        if (selectedProject && selectedProject._id === projectId) {
             setSelectedProject(prev => ({ ...prev, status: newStatus }));
        }
        // Refresh global stats to update pending count
        fetchData();
      } catch (error) {
          console.error('Error updating project status:', error);
          showAlert({ type: 'error', message: 'Failed to update project status' });
      }
  };


  const fetchMentorStats = async () => {
    try {
      setLoading(true);
      setLoading(true);
      const data = await getMentorStats();
      if (data.success) {
        setMentorStats(data.mentors);
        setActiveView('mentors');
      }
    } catch (error) {
      console.error('Error fetching mentor stats:', error);
      showAlert({ type: 'error', title: 'Error', message: 'Failed to fetch detailed mentor stats' });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async (type = 'all') => {
    try {
      setLoading(true);
      setLoading(true);
      const data = await getUserStats(type);

      if (data.success) {
        setUserStats(data.users);
        setActiveView(type === 'premium' ? 'premium' : 'users');
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      showAlert({ type: 'error', title: 'Error', message: 'Failed to fetch detailed user stats' });
    } finally {
      setLoading(false);
    }
  };

  if (loading && activeView === 'dashboard') {
     return (
        <div className="flex justify-center items-center min-h-screen">
          <FaSpinner className="animate-spin text-4xl text-blue-600" />
        </div>
      );
  }

  const getPageTitle = () => {
    switch (activeView) {
      case 'mentors': return 'Mentor Analytics';
      case 'users': return 'All Users';
      case 'premium': return 'Premium Users';
      case 'projects': return 'Project Proposals';
      default: return 'Admin Dashboard';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-12 relative">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-8">
           {activeView !== 'dashboard' && (
              <button 
                onClick={() => setActiveView('dashboard')}
                className="mr-4 p-2 rounded-full hover:bg-gray-200 text-gray-600 transition-colors"
                title="Back to Dashboard"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
              </button>
           )}
           <h1 className="text-3xl font-bold text-gray-900">
             {getPageTitle()}
           </h1>
        </div>

        {activeView === 'dashboard' ? (
          <>
            {/* Stats Grid */}
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6 mb-12">
                <StatCard 
                  title="Total Users" 
                  value={159} 
                  icon={<FaUserGraduate className="text-blue-500" />} 
                  color="bg-blue-50"
                  onClick={() => fetchUserStats('all')}
                  clickable
                />
                <StatCard 
                  title="Premium Users" 
                  value={13} 
                  icon={<FaCrown className="text-yellow-500" />} 
                  color="bg-yellow-50"
                  onClick={() => fetchUserStats('premium')}
                  clickable
                />
                <StatCard 
                  title="Total Mentors" 
                  value={stats.totalMentors} 
                  icon={<FaUserTie className="text-purple-500" />} 
                  color="bg-purple-50"
                  onClick={fetchMentorStats}
                  clickable
                />
                <StatCard 
                  title="Pending Requests" 
                  value={stats.pendingRequests} 
                  icon={<FaClipboardList className="text-orange-500" />} 
                  color="bg-orange-50"
                />
                 <StatCard 
                  title="Pending Projects" 
                  value={stats.pendingProjects || 0} 
                  icon={<FaCodeBranch className="text-teal-500" />} 
                  color="bg-teal-50"
                  onClick={fetchProjects}
                  clickable
                />
              </div>
            )}

            {/* Applications Table */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden mb-12">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">Mentor Applications</h2>
                 <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                  {applications.length} Pending
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 text-gray-600 font-medium">
                    <tr>
                      <th className="p-4">Name</th>
                      <th className="p-4">Expertise</th>
                      <th className="p-4">Contact</th>
                      <th className="p-4">Experience</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {applications.map((app) => (
                      <tr key={app._id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-4">
                          <div className="font-medium text-gray-900">{app.name}</div>
                          <div className="text-xs text-blue-600">
                            <a href={app.linkedin} target="_blank" rel="noreferrer">LinkedIn Profile</a>
                          </div>
                        </td>
                        <td className="p-4 text-sm">{app.expertise}</td>
                        <td className="p-4">
                          <div className="text-sm">{app.email}</div>
                          <div className="text-xs text-gray-500">{app.mobile}</div>
                        </td>
                        <td className="p-4 text-sm">{app.experience}</td>
                        <td className="p-4">
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                            {app.applicationStatus}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleApplicationStatus(app._id, 'approved')}
                              className="p-1.5 bg-green-100 text-green-600 rounded hover:bg-green-200"
                              title="Approve"
                            >
                              <FaCheck size={14} />
                            </button>
                            <button 
                              onClick={() => handleApplicationStatus(app._id, 'rejected')}
                              className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200"
                              title="Reject"
                            >
                              <FaTimes size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {applications.length === 0 && (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-gray-500">
                          No pending applications found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
    
            {/* Requests Table */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
               <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-900">Recent Mentor Requests</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 text-gray-600 font-medium">
                    <tr>
                      <th className="p-4">Student</th>
                      <th className="p-4">Mentor</th>
                      <th className="p-4">Topic</th>
                      <th className="p-4">Requested Time</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {requests.map((req) => (
                      <tr key={req._id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-4">
                          <div className="font-medium text-gray-900">{req.student?.username || 'Unknown'}</div>
                        </td>
                        <td className="p-4">{req.mentor?.name || 'Unknown'}</td>
                        <td className="p-4 font-medium text-blue-600">{req.topic}</td>
                        <td className="p-4">
                          <div className="text-sm">{req.meetingDay}</div>
                          <div className="text-xs text-gray-500">{req.meetingTime}</div>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold
                            ${req.status === 'pending' ? 'bg-orange-100 text-orange-700' : 
                              req.status === 'accepted' ? 'bg-green-100 text-green-700' :
                              req.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}
                          `}>
                            {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                          </span>
                        </td>
                        <td className="p-4">
                          {req.status === 'pending' && (
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => handleStatusUpdate(req._id, 'accepted')}
                                className="p-1.5 bg-green-100 text-green-600 rounded hover:bg-green-200"
                                title="Accept"
                              >
                                <FaCheck size={14} />
                              </button>
                               <button 
                                onClick={() => handleStatusUpdate(req._id, 'rejected')}
                                className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200"
                                title="Reject"
                              >
                                <FaTimes size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {requests.length === 0 && (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-gray-500">
                          No requests found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : activeView === 'projects' ? (
           /* Projects Management Table */
           <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-600 font-medium">
                            <tr>
                                <th className="p-4">Title</th>
                                <th className="p-4">Submitted By</th>
                                <th className="p-4">Difficulty</th>
                                <th className="p-4">Submitted At</th>
                                <th className="p-4">Status</th>
                                <th className="p-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {projects.map((project) => (
                                <tr key={project._id} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-4 font-medium text-gray-900">
                                        <button 
                                            onClick={() => setSelectedProject(project)}
                                            className="text-blue-600 hover:text-blue-800 font-medium text-left hover:underline"
                                        >
                                            {project.title}
                                        </button>
                                    </td>
                                     <td className="p-4">
                                        <div className="text-sm font-medium">{project.submittedBy?.username || 'Unknown'}</div>
                                        <div className="text-xs text-gray-500">{project.submittedBy?.email}</div>
                                    </td>
                                    <td className="p-4 text-sm">{project.difficulty}</td>
                                    <td className="p-4 text-sm text-gray-500">{new Date(project.createdAt).toLocaleDateString()}</td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-semibold
                                            ${project.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                                              project.status === 'published' ? 'bg-green-100 text-green-700' :
                                              'bg-red-100 text-red-700'}
                                        `}>
                                            {project.status.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <button 
                                            onClick={() => setSelectedProject(project)}
                                            className="px-3 py-1 bg-gray-100 text-gray-600 rounded-md text-sm hover:bg-gray-200"
                                        >
                                            View Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                             {projects.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="p-8 text-center text-gray-500">No projects found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
           </div>
        ) : activeView === 'mentors' ? (
          /* Mentors List with Detailed Stats */
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
             <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50 text-gray-600 font-medium">
                    <tr>
                      <th className="p-4">Mentor Name</th>
                      <th className="p-4">Total Requests</th>
                      <th className="p-4">Accepted</th>
                      <th className="p-4">Pending</th>
                      <th className="p-4">Rejected</th>
                      <th className="p-4">Rating</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {mentorStats.map((mentor) => (
                      <tr key={mentor._id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-4">
                          <div className="font-bold text-gray-900">{mentor.name}</div>
                          <div className="text-xs text-gray-500">{mentor.email}</div>
                        </td>
                        <td className="p-4 font-semibold text-lg">{mentor.totalRequests}</td>
                        <td className="p-4 text-green-600 font-medium">{mentor.accepted}</td>
                        <td className="p-4 text-orange-600 font-medium">{mentor.pending}</td>
                        <td className="p-4 text-red-600 font-medium">{mentor.rejected}</td>
                        <td className="p-4 text-yellow-600 font-medium">★ {mentor.rating}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
             </div>
          </div>
        ) : (
          /* Users List (All or Premium) */
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
                 {/* Active Users Table (Default for 'users' and 'premium') */}
                <table className="w-full text-left">
                  <thead className="bg-gray-50 text-gray-600 font-medium">
                    <tr>
                      <th className="p-4">Username</th>
                      <th className="p-4">Email</th>
                      <th className="p-4">Plan Type</th>
                      <th className="p-4">Skill Analyses</th>
                      <th className="p-4">Roadmaps Generated</th>
                      <th className="p-4">Joined At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {userStats.map((user) => (
                      <tr key={user._id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-4 font-medium text-gray-900">{user.username}</td>
                        <td className="p-4 text-gray-600">{user.email}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold
                            ${user.planType === 'premium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}
                          `}>
                            {user.planType.charAt(0).toUpperCase() + user.planType.slice(1)}
                          </span>
                        </td>
                        <td className="p-4 text-center">{user.skillAnalysisCount}</td>
                        <td className="p-4 text-center">{user.roadmapGenCount}</td>
                        <td className="p-4 text-sm text-gray-500">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                    {userStats.length === 0 && (
                      <tr>
                        <td colSpan="6" className="p-8 text-center text-gray-500">
                          No users found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
            </div>
          </div>
        )}
      </div>

       {/* Project Modal */}
       {selectedProject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedProject.title}</h2>
                <div className="flex items-center space-x-3 mt-1 text-sm text-gray-500">
                  <span>Submitted by: <strong>{selectedProject.submittedBy?.username || 'Unknown'}</strong></span>
                  <span>•</span>
                  <span>{new Date(selectedProject.createdAt).toLocaleDateString()}</span>
                  <span>•</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold
                      ${selectedProject.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                        selectedProject.status === 'published' ? 'bg-green-100 text-green-700' :
                        'bg-red-100 text-red-700'}
                    `}>
                        {selectedProject.status.toUpperCase()}
                    </span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedProject(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              
              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-xl border border-gray-100">
                  {selectedProject.description}
                </p>
              </div>

               {/* Grid Details */}
               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Tech Stack</h3>
                        <div className="flex flex-wrap gap-2">
                            {selectedProject.techStack.map((tech, i) => (
                                <span key={i} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium border border-blue-100">
                                    {typeof tech === 'string' ? tech : tech.name}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div>
                        <h3 className="font-semibold text-gray-900 mb-2">Details</h3>
                        <div className="space-y-2 text-sm">
                             <div className="flex justify-between border-b border-gray-100 pb-1">
                                <span className="text-gray-500">Difficulty</span>
                                <span className="font-medium">{selectedProject.difficulty}</span>
                             </div>
                             <div className="flex justify-between border-b border-gray-100 pb-1">
                                <span className="text-gray-500">Stars</span>
                                <span className="font-medium">{selectedProject.stars}</span>
                             </div>
                             <div className="flex justify-between border-b border-gray-100 pb-1">
                                <span className="text-gray-500">Forks</span>
                                <span className="font-medium">{selectedProject.forks}</span>
                             </div>
                        </div>
                    </div>
               </div>

                {/* Guidelines */}
               <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Contribution Guidelines</h3>
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 text-sm text-gray-600 space-y-1">
                      {selectedProject.guidelines.map((line, i) => (
                          <div key={i} className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>{line}</span>
                          </div>
                      ))}
                  </div>
               </div>

                {/* Rules */}
               <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Rules</h3>
                   <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 text-sm text-gray-600 space-y-1">
                      {selectedProject.rules.map((line, i) => (
                          <div key={i} className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>{line}</span>
                          </div>
                      ))}
                  </div>
               </div>
               
               {/* Links */}
               <div>
                  <a 
                    href={selectedProject.githubLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-full p-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors gap-2"
                  >
                     <FaGithub size={20} />
                     <span>View on GitHub</span>
                     <FaExternalLinkAlt size={12} />
                  </a>
               </div>

            </div>

             {/* Footer Actions */}
             <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-100 flex justify-end gap-3 rounded-b-2xl">
                {selectedProject.status === 'pending' ? (
                  <>
                    <button 
                        onClick={() => handleProjectStatus(selectedProject._id, 'rejected')}
                        className="px-6 py-2.5 rounded-xl border border-red-200 text-red-600 font-medium hover:bg-red-50 transition-colors"
                    >
                        Reject
                    </button>
                    <button 
                         onClick={() => handleProjectStatus(selectedProject._id, 'published')}
                         className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-green-600 to-green-500 text-white font-medium hover:shadow-lg hover:shadow-green-500/20 transition-all transform hover:-translate-y-0.5"
                    >
                        Approve & Publish
                    </button>
                  </>
                ) : (
                    <button 
                         onClick={() => handleProjectStatus(selectedProject._id, selectedProject.status === 'published' ? 'rejected' : 'published')}
                         className={`px-6 py-2.5 rounded-xl text-white font-medium hover:shadow-lg transition-all transform hover:-translate-y-0.5
                             ${selectedProject.status === 'published' ? 'bg-red-600 hover:shadow-red-500/20' : 'bg-green-600 hover:shadow-green-500/20'}
                         `}
                    >
                        {selectedProject.status === 'published' ? 'Unpublish' : 'Republish'}
                    </button>
                )}
             </div>

          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon, color, onClick, clickable }) => (
  <div 
    onClick={clickable ? onClick : undefined}
    className={`bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center space-x-4 
      ${clickable ? 'cursor-pointer hover:shadow-md hover:border-blue-200 transition-all active:scale-95' : ''}
    `}
  >
    <div className={`p-4 rounded-lg ${color} text-xl`}>
      {icon}
    </div>
    <div>
      <p className="text-gray-500 text-sm font-medium">{title}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  </div>
);

export default AdminDashboard;
